import {Box, BoxProps, ButtonProps, Collapsible, DrawerTrigger, Icon, Link, Separator, Stack} from "@chakra-ui/react";
import NextLink from "next/link";
import Button from "../Button";
import {Logo} from "../Logo";
import type {DropdownPage, Page} from "./links";
import {LeftNav, Nav, NavContext} from "./Common";
import {useContext, useState} from "react";
import {DrawerBackdrop, DrawerBody, DrawerCloseTrigger, DrawerContent, DrawerHeader, DrawerRoot} from "../ui/drawer";
import {ChevronDownIcon, ChevronUpIcon} from "@heroicons/react/24/solid";

const MobileNavLinkButton = (props: {to?: string} & ButtonProps) => (
  <Button
    px={6}
    py={8}
    rounded="none"
    variant="ghost"
    size="md"
    fontWeight="600"
    justifyContent="flex-start"
    fontSize="md"
    {...props}
  />
);

const DropdownButton = ({page, ...props}: {page: DropdownPage} & ButtonProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible.Root open={open} onOpenChange={e => setOpen(e.open)}>
      <Collapsible.Trigger asChild>
        <MobileNavLinkButton {...props} w="full">
          <Box flex={1} textAlign="left">
            {page.text}
          </Box>
          <Icon>{open ? <ChevronUpIcon /> : <ChevronDownIcon />}</Icon>
        </MobileNavLinkButton>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Stack bg="gray.800" borderY="1px solid black">
          <page.dropdown bg="transparent" />
        </Stack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const MobileNavLink = ({page, ...props}: {page: Page} & ButtonProps) =>
  page.type === "link" ? (
    <MobileNavLinkButton asChild {...props}>
      <NextLink href={page.href}>{page.text}</NextLink>
    </MobileNavLinkButton>
  ) : (
    <DropdownButton page={page} {...props} />
  );

const MobileNavIcon = ({light, ...props}: {light?: boolean} & BoxProps) => (
  <Box cursor="pointer" p={2} mx={-2} _hover={{bg: "whiteAlpha.200"}} {...props}>
    <Box
      asChild
      aria-hidden="true"
      h="15px"
      w="14px"
      overflow="visible"
      stroke={light ? "white" : "gray.700"}
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <svg>
        <path d="M0 1H14M0 7H14M0 13H14" />
      </svg>
    </Box>
  </Box>
);

const MobileNavigationSideMenu = ({links}: {links: Page[]}) => {
  const light = useContext(NavContext);

  const home: Page = {type: "link", text: "Home", href: "/"};
  const login: Page = {type: "link", text: "Sign in", href: "https://app.platformed.com"};
  const contact: Page = {type: "link", text: "Contact us", href: "/contact"};

  return (
    <>
      <DrawerRoot placement="end" size="full">
        <DrawerTrigger>
          <MobileNavIcon light={light} />
        </DrawerTrigger>
        <DrawerBackdrop />
        <DrawerContent>
          <DrawerHeader px={8} pt={4} display="flex" alignItems="center" justifyContent="space-between">
            <Link asChild outline="none" _focusVisible={{outline: "none"}}>
              <NextLink href="/">
                <Logo h={6} showText />
              </NextLink>
            </Link>
            <DrawerCloseTrigger mr={3} mt={2} />
          </DrawerHeader>
          <DrawerBody px={0}>
            <Stack gap={0}>
              <MobileNavLink page={home} />
              {links.map((page, i) => (
                <MobileNavLink page={page} key={i} />
              ))}
              <Separator />
              <MobileNavLink page={login} />
              <MobileNavLink
                page={contact}
                color="white"
                bgGradient="linear-gradient(170deg, {colors.green.400}, {colors.green.500})"
                _hover={{bgGradient: "linear-gradient(170deg, {colors.green.500}, {colors.green.600})"}}
              />
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </DrawerRoot>
    </>
  );
};

const MobileNav = ({links, light, ...props}: {links: Page[]; light?: boolean} & BoxProps) => (
  <Nav light={light} {...props}>
    <LeftNav />
    <MobileNavigationSideMenu links={links} />
  </Nav>
);

export default MobileNav;
